import {
  CellTemplate,
  Compatible,
  Uncertain,
  UncertainCompatible,
  getCharFromKeyCode,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
} from "@silevis/reactgrid";
import { ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { CellCustomType } from "@lhd/components/CloneApp/pages/SheetApp/type";
import { useRowHeight } from "@lhd/components/CloneApp/fields/TableSheet/RowHeightContext";
import TextareaAutosize from "react-textarea-autosize";

function TextAreaViewCell({ cell }: { cell: Compatible<CellCustomType> }) {
  const { updateRowHeight } = useRowHeight();
  const prevTextRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!cell.clientId || !cell.rowIndex) return;

    updateRowHeight(String(cell.clientId), cell.rowIndex);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prev = prevTextRef.current;
    prevTextRef.current = cell.text;
    if (prev === undefined || prev === cell.text) return;
    if (!cell.clientId || !cell.rowIndex) return;
    updateRowHeight(String(cell.clientId), cell.rowIndex);
  }, [cell.text]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {cell.text}
    </div>
  );
}

function TextAreaEditCell({
  cell,
  onCellChanged,
}: {
  cell: Compatible<CellCustomType>;
  onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void;
}) {
  const { updateRowHeight, getManualHeight } = useRowHeight();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Restore a previously manual-resized height after the autosize lib's own
  // mount layout effect (child-first) has already set its content-fit height.
  // Reactgrid freezes the edited cell's own props for the whole edit session
  // (it doesn't refresh `cell` from the live rows prop while editing, and the
  // commit on blur can re-write a stale `cell.rowHeight`), so the manual height
  // is read from TableSheet's own ref instead of trusting `cell.rowHeight`.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    const manualHeight = getManualHeight(cell.clientId);

    if (!el || !manualHeight) return;
    if (el.offsetHeight >= manualHeight) return;
    el.style.setProperty("height", `${manualHeight}px`, "important");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = textareaRef.current;
    if (!el || !cell.clientId || !cell.rowIndex) return;

    // reactgrid renders the editor inside an overlay whose height is fixed to the
    // current row height, so we measure the textarea itself, not that overlay.
    const observer = new ResizeObserver(() => {
      updateRowHeight(String(cell.clientId), cell.rowIndex, el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cell.clientId, cell.rowIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TextareaAutosize
      disabled={!!cell.nonEditable}
      ref={(ref) => {
        textareaRef.current = ref;
        if (ref) {
          setTimeout(() => {
            ref.focus();
          }, 0);
        }
      }}
      defaultValue={cell.text}
      style={{
        width: "100%",
        outline: "none",
        resize: "vertical",
        border: "1px solid #fff",
        fontSize: "inherit",
        fontFamily: "inherit",
        background: "transparent",
        boxSizing: "border-box",
        backgroundColor: "#fff",
      }}
      onChange={(e) => {
        cell.onChange?.();
        onCellChanged(
          {
            ...cell,
            text: e.currentTarget.value.trim(),
          },
          false,
        );
      }}
      onBlur={(e) =>
        onCellChanged(
          {
            ...cell,
            text: e.currentTarget.value.trim(),
          },
          true,
        )
      }
      onKeyDown={(e) => {
        if (
          isAlphaNumericKey(e.keyCode) ||
          isNavigationKey(e.keyCode) ||
          e.key === "Enter"
        ) {
          e.stopPropagation();
        }
      }}
      onFocus={(e) => {
        const input = e.target;
        input.setSelectionRange(input.value.length, input.value.length);
      }}
      onCopy={(e) => e.stopPropagation()}
      onCut={(e) => e.stopPropagation()}
      onPaste={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}

export class TextAreaCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    return {
      value: NaN,
      ...(uncertainCell as CellCustomType),
    };
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    if (cell.nonEditable) {
      return {
        cell,
        enableEditMode: false,
      };
    }
    const char = getCharFromKeyCode(keyCode, shift);
    const isIME = keyCode === 229 || keyCode === 299;
    if (isIME) {
      return {
        cell,
        enableEditMode: true,
      };
    }
    if (
      ctrl ||
      alt ||
      !isAlphaNumericKey(keyCode) ||
      (shift && keyCode === keyCodes.SPACE)
    ) {
      return {
        cell,
        enableEditMode:
          keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
      };
    }
    cell.onChange?.();
    return {
      cell: {
        ...cell,
        text: cell.text + (shift ? char : char.toLowerCase()),
      },
      enableEditMode: true,
    };
  }

  getClassName(cell: Compatible<CellCustomType>): string {
    return cell.className || "";
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    return this.getCompatibleCell({
      ...cell,
      ...cellToMerge,
    });
  }

  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ): ReactNode {
    if (!isInEditMode) {
      return <TextAreaViewCell cell={cell} />;
    }
    return <TextAreaEditCell cell={cell} onCellChanged={onCellChanged} />;
  }
}
